import PageMeta from '@/components/PageMeta';
import SectionHeader from '@/components/SectionHeader';
import { Link } from 'react-router-dom';

const SCENARIOS = [
  {
    t: 'Recovery',
    b: 'Rebuilding everyday movement after illness, treatment, or a hospital stay, when the body feels less familiar than it used to.',
  },
  {
    t: 'Rehabilitation',
    b: 'Following exercises set by a physiotherapist or allied-health professional with clearer feedback between appointments.',
  },
  {
    t: 'Reduced mobility',
    b: 'Living with limited movement, pain, or fatigue and wanting structured awareness of how everyday movements look.',
  },
  {
    t: 'Rebuilding strength',
    b: 'Returning to basic strength work after a long period away — with attention paid to control, not just effort.',
  },
  {
    t: 'Body awareness',
    b: 'Better understanding visible movement patterns, posture, and how the body moves through familiar exercises.',
  },
  {
    t: 'Returning after interruption',
    b: 'Coming back to ordinary movement after surgery, an injury, ageing-related changes, or a significant life event.',
  },
  {
    t: 'Everyday confidence',
    b: 'Understanding how everyday movement looks in plain language, without complicated jargon or numbers.',
  },
];

export default function ForIndividuals() {
  return (
    <div>
      <PageMeta
        title="For individuals"
        description="Valanti Labs is being developed for many different people. Movement can feel uncertain for many different reasons."
      />
      <section className="vl-container py-16 lg:py-24">
        <p className="mono-label text-vl-violet">For individuals</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-vl-ink max-w-3xl">
          Movement can feel uncertain for many different reasons.
        </h1>
        <p className="mt-6 text-lg text-vl-ink/70 max-w-2xl leading-relaxed">
          Valanti Labs is being designed to help in many of those moments — without trying to define people by a
          single diagnosis or limitation.
        </p>
      </section>

      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-20 lg:py-28">
          <SectionHeader kicker="Where it may help" title="A wide range of moments." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SCENARIOS.map((s) => (
              <div key={s.t} className="rounded-3xl bg-vl-surface border hairline p-7 hover:-translate-y-0.5 transition-transform">
                <h3 className="font-serif-display text-2xl text-vl-ink">{s.t}</h3>
                <p className="text-sm text-vl-ink/70 mt-3 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="vl-container py-24 lg:py-32 max-w-3xl">
        <p className="mono-label text-vl-violet">Important disclaimer</p>
        <h2 className="font-serif-display text-3xl sm:text-4xl text-vl-ink mt-4 leading-tight">
          Please continue to follow professional advice.
        </h2>
        <div className="mt-6 space-y-4 text-vl-ink/75 leading-relaxed">
          <p>
            Valanti Labs is not a substitute for a doctor, physiotherapist, or other qualified professional.
          </p>
          <p>
            Users should continue to follow professional guidance and should not begin or continue an exercise they
            have been told is unsuitable.
          </p>
          <p>
            Users experiencing concerning symptoms — including new pain, dizziness, breathlessness, or any change that
            feels significant — should stop and seek appropriate professional advice.
          </p>
        </div>
        <Link
          to="/early-access"
          className="inline-flex items-center gap-2 mt-10 rounded-full bg-vl-ink text-vl-bg px-6 py-3.5 text-sm font-medium hover:bg-vl-violet transition-colors"
        >
          Join the early access list
        </Link>
      </section>
    </div>
  );
}
